import { useState, useEffect } from "react";
import { Settings, GripVertical, RotateCcw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import type { ColumnVisibility } from "@/types/transaction";

interface ColumnConfig {
  key: keyof ColumnVisibility;
  label: string;
  visible: boolean;
  locked?: boolean; // Some columns should always be visible
  order: number;
}

interface ColumnConfigDialogProps {
  columnVisibility: ColumnVisibility;
  onApply: (config: ColumnVisibility, order: string[]) => void;
  columnOrder?: string[];
}

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: "referenceNumber", label: "Reference Number", visible: true, locked: true, order: 0 },
  { key: "transactionDate", label: "Transaction Date", visible: true, order: 1 },
  { key: "dueDate", label: "Due Date", visible: true, order: 2 },
  { key: "transactionType", label: "Type", visible: true, order: 3 },
  { key: "amount", label: "Gross", visible: true, order: 4 },
  { key: "outstandingAmount", label: "Outstanding", visible: true, order: 5 },
  { key: "status", label: "Status", visible: true, order: 6 },
  { key: "description", label: "Description", visible: false, order: 7 },
  { key: "accountCode", label: "Account", visible: false, order: 8 },
  { key: "netAmount", label: "Net", visible: false, order: 9 },
  { key: "vatAmount", label: "Tax", visible: false, order: 10 },
  { key: "customerReference", label: "Customer Reference", visible: false, order: 11 },
  { key: "periodNumber", label: "Period Number", visible: false, order: 12 },
  { key: "year", label: "Year", visible: false, order: 13 },
  { key: "onDispute", label: "On Dispute", visible: false, order: 14 },
  { key: "subLedger", label: "Sub Ledger", visible: false, order: 15 },
  { key: "orderNumber", label: "Order Number", visible: false, order: 16 },
  { key: "createdBy", label: "Created By", visible: false, order: 17 },
  { key: "modifiedBy", label: "Modified By", visible: false, order: 18 },
  { key: "customerName", label: "Customer Name", visible: false, order: 19 },
  { key: "actions", label: "Actions", visible: false, order: 20 },
];

export function ColumnConfigDialog({
  columnVisibility,
  onApply,
  columnOrder = [],
}: ColumnConfigDialogProps) {
  const [open, setOpen] = useState(false);
  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Initialize columns from props
  useEffect(() => {
    if (open) {
      const initialColumns = DEFAULT_COLUMNS.map((col) => ({
        ...col,
        visible: columnVisibility[col.key],
      }));

      // Apply custom order if provided
      if (columnOrder.length > 0) {
        const orderedColumns = columnOrder
          .map((key) => initialColumns.find((col) => col.key === key))
          .filter((col): col is ColumnConfig => col !== undefined);

        // Add any columns not in the order array
        const remainingColumns = initialColumns.filter(
          (col) => !columnOrder.includes(col.key)
        );

        setColumns([...orderedColumns, ...remainingColumns]);
      } else {
        setColumns(initialColumns);
      }
    }
  }, [open, columnVisibility, columnOrder]);

  const handleToggleColumn = (key: keyof ColumnVisibility) => {
    setColumns((prev) =>
      prev.map((col) => (col.key === key ? { ...col, visible: !col.visible } : col))
    );
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      const newColumns = [...columns];
      const [draggedColumn] = newColumns.splice(draggedIndex, 1);
      newColumns.splice(dragOverIndex, 0, draggedColumn);
      setColumns(newColumns);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleReset = () => {
    const resetColumns = DEFAULT_COLUMNS.map((col) => ({ ...col }));
    setColumns(resetColumns);
  };

  const handleApply = () => {
    const newVisibility: ColumnVisibility = columns.reduce(
      (acc, col) => ({
        ...acc,
        [col.key]: col.visible,
      }),
      {} as ColumnVisibility
    );

    const newOrder = columns.map((col) => col.key);
    onApply(newVisibility, newOrder);
    setOpen(false);
  };

  const visibleCount = columns.filter((col) => col.visible).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Settings className="h-4 w-4" />
          Configure Columns
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Configure Table Columns</DialogTitle>
          <DialogDescription>
            Show, hide, and reorder columns in the transaction table. Drag columns to reorder
            them.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {visibleCount} of {columns.length} columns visible
            </div>
            <Button variant="ghost" size="sm" onClick={handleReset} className="gap-2">
              <RotateCcw className="h-4 w-4" />
              Reset to Default
            </Button>
          </div>

          <Separator />

          <ScrollArea className="h-[400px]">
            <ScrollAreaViewport className="h-full pr-4">
              <div className="space-y-2">
                {columns.map((column, index) => (
                  <div
                    key={column.key}
                    draggable={!column.locked}
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`
                      flex items-center justify-between p-3 rounded-lg border bg-card
                      ${!column.locked ? "cursor-move hover:bg-accent" : "cursor-default"}
                      ${draggedIndex === index ? "opacity-50" : ""}
                      ${dragOverIndex === index ? "border-primary" : ""}
                      transition-all
                    `}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      {!column.locked && (
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                      )}
                      <Label
                        htmlFor={`column-${column.key}`}
                        className={`flex-1 ${column.locked ? "ml-7" : ""} ${
                          !column.visible ? "text-muted-foreground" : ""
                        }`}
                      >
                        {column.label}
                        {column.locked && (
                          <span className="ml-2 text-xs text-muted-foreground">(Required)</span>
                        )}
                      </Label>
                    </div>
                    <Switch
                      id={`column-${column.key}`}
                      checked={column.visible}
                      onCheckedChange={() => handleToggleColumn(column.key)}
                      disabled={column.locked}
                    />
                  </div>
                ))}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply}>Apply Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
